import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaSearch } from "react-icons/fa";
import {
  getAllRadiologyReport,
  getOutSourceToRadiologyReport,
  getOutSourceFromRadiologyReport,
} from "../../../../store/slices/radiologyReport";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import styles from "../report/scss/radiologyReport.module.scss";
import Table from "../../../common/Table/table";

const LabReport = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [reportFromData, setReportFromData] = useState({
    fromDate: "",
    toDate: "",
  });
  const [pageLimit, setPageLimit] = useState(10);
  const { clinicInfo } = useSelector((state) => state.clinic);
  const { token } = useSelector((state) => state.auth);
  const clinicId =clinicInfo?.[0]?.clinicId ||null;
  const {
    radiologyReports,
    searchMessage,
    currentPage,
    totalPages,
    totalCount,
  } = useSelector((state) => state.radiologyReport);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fetchReports = useCallback(
    (page = 1, lim = pageLimit, search = searchValue) => {
      if (clinicId && token) {
        const params = {
          clinicId,
          token,
          currentPage: page,
          limit: lim,
          searchQuery: search || undefined,
          fromDate: reportFromData.fromDate,
          toDate: reportFromData.toDate,
        };

        if (activeTab === 0) {
          dispatch(getAllRadiologyReport(params));
        } else if (activeTab === 1) {
          dispatch(getOutSourceToRadiologyReport(params));
        } else if (activeTab === 2) {
          dispatch(getOutSourceFromRadiologyReport(params));
        }
      }
    },
    [
      dispatch,
      clinicId,
      token,
      activeTab,
      searchValue,
      reportFromData,
      pageLimit,
    ],
  );
  useEffect(() => {
    fetchReports(1, pageLimit, searchValue);
  }, [dispatch, clinicId, token, activeTab]);

  useEffect(() => {
    dispatch(getAllRadiologyReport({ clinicId, token }));
  }, [dispatch, clinicId, token]);
  const handlePageChange = (page) => {
    fetchReports(page, pageLimit, searchValue);
  };

  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    fetchReports(1, newLimit, searchValue);
  };

  const handleShowGlobalViewModal = useCallback(
    (item) => {
      if (item?.reportType === "allReports") {
        sessionStorage.setItem("allReportsId", item?.id);
        sessionStorage.removeItem("outSourceToId");
        sessionStorage.removeItem("outSourceFromId");
      } else if (item?.reportType === "outSourceTo") {
        sessionStorage.setItem("outSourceToId", item?.id);
        sessionStorage.removeItem("outSourceFromId");
        sessionStorage.removeItem("allReportsId");
      } else if (item?.reportType === "outSourceFrom") {
        sessionStorage.setItem("outSourceFromId", item?.id);
        sessionStorage.removeItem("outSourceToId");
        sessionStorage.removeItem("allReportsId");
      }
      sessionStorage.setItem("patientId", item?.patientId);
      sessionStorage.setItem("appointmentId", item?.appointmentId);
      navigate("/viewRadiologyReport");
    },
    [navigate],
  );

  const handleEditLabReport = useCallback(
    (id) => {
      sessionStorage.setItem("radiologyReportId", id.id);
      navigate("/editRadiologyReport");
    },
    [navigate],
  );

  const tableHead = useMemo(() => {
    switch (activeTab) {
      case 0:
        return [
          { title: "APT Id", key: "appointmentId" },
          { title: "Patient Name", key: "name" },
          { title: "Appt Date", key: "appDate" },
          { title: "Service Details", key: "serviceList" },
          { title: "Report Status", key: "labReportStatus" },
        ];
      case 1:
        return [
          { title: "APT Id", key: "appointmentId" },
          { title: "Patient Name", key: "name" },
          { title: "Service Details", key: "serviceList" },
          { title: "Appt Date", key: "appDate" },
          { title: "OutSource To", key: "outSourceTo" },
          { title: "Report Status", key: "labReportStatus" },
        ];
      case 2:
        return [
          { title: "APT Id", key: "appointmentId" },
          { title: "Patient Name", key: "name" },
          { title: "Service Details", key: "serviceList" },
          { title: "Appt Date", key: "appDate" },
          { title: "OutSource From", key: "outSourceFrom" },
          { title: "Report Status", key: "labReportStatus" },
        ];
      default:
        return [];
    }
  }, [activeTab]);

  const tableBody = useMemo(() => {
    switch (activeTab) {
      case 0:
        return (
          radiologyReports &&
          (radiologyReports?.map((item) => ({
            id: item._id,
            appointmentId: item.appointmentId,
            name: item?.patientId?.userId?.name,
            serviceList: (
              item?.radiologyServiceInfo?.serviceDetails?.map(
                (service) => service.serviceDetailsName,
              ) || []
            ).join(" "),
            appDate: new Date(item.appDate)?.toISOString()?.split("T")[0],
            status: item.status,
            patientId: item?.patientId?._id,
            labReportStatus: item?.labReportStatus,
            reportType: "allReports",
          })) ||
            [])
        );
      case 1:
        return (
          radiologyReports &&
          (radiologyReports?.map((item) => ({
            id: item._id,
            appointmentId: item.appointmentId,
            name: item.patientId.userId.name,
            serviceList: (
              item?.radiologyServiceInfo?.serviceDetails?.map(
                (service) => service.serviceDetailsName,
              ) || []
            ).join(", "),
            appDate: new Date(item.appDate).toISOString().split("T")[0],
            totalAmount: item.totalAmount,
            outSourceTo: item?.labForOutsource?.clinicName,
            status: item.status,
            patientId: item?.patientId?._id,
            labReportStatus: item?.labReportStatus,
            reportType: "outSourceTo",
          })) ||
            [])
        );
      case 2:
        return (
          radiologyReports &&
          (radiologyReports?.map((item) => ({
            id: item._id,
            appointmentId: item.appointmentId,
            name: item.patientId.userId.name,
            serviceList: (
              item?.radiologyServiceInfo?.serviceDetails?.map(
                (service) => service.serviceDetailsName,
              ) || []
            ).join(", "),
            appDate: new Date(item.appDate).toISOString().split("T")[0],
            totalAmount: item.totalAmount,
            outSourceFrom: item?.outsourceBy?.clinicName,
            status: item.status,
            patientId: item?.patientId?._id,
            labReportStatus: item?.labReportStatus,
            reportType: "outSourceFrom",
          })) ||
            [])
        );
      default:
        return [];
    }
  }, [activeTab, radiologyReports]);

  const handleTab = (tab) => {
    setActiveTab(tab);
    if (tab === 1) {
      dispatch(getOutSourceToRadiologyReport({ clinicId, token }));
    } else if (tab === 2) {
      dispatch(getOutSourceFromRadiologyReport({ clinicId, token }));
    }
  };

  const tableData = useMemo(
    () => (
      <Table
        tableHead={tableHead}
        tableBody={tableBody}
        addModal
        handleEdit={handleEditLabReport}
        handleShowGlobalViewModal={handleShowGlobalViewModal}
        errorMessage={searchMessage}
        searchValue={searchValue}
        filterDateForSearch={reportFromData.fromDate || reportFromData.toDate}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={pageLimit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        showPagination={true}
        showLimitDropdown={true}
      />
    ),
    [
      tableHead,
      handleEditLabReport,
      handleShowGlobalViewModal,
      tableBody,
      searchMessage,
      searchValue,
      reportFromData,
      currentPage,
      totalPages,
      totalCount,
      pageLimit,
    ],
  );

  const handleFilter = async () => {
    fetchReports(1, pageLimit, searchValue);
    if (activeTab === 1) {
      await dispatch(
        getOutSourceToRadiologyReport({
          clinicId,
          fromDate: reportFromData.fromDate,
          toDate: reportFromData.toDate,
          token,
        }),
      );
    } else if (activeTab === 2) {
      await dispatch(
        getOutSourceFromRadiologyReport({
          clinicId,
          fromDate: reportFromData.fromDate,
          toDate: reportFromData.toDate,
          token,
        }),
      );
    } else {
      if (reportFromData.fromDate && reportFromData.toDate) {
        await dispatch(
          getAllRadiologyReport({
            clinicId,
            fromDate: reportFromData.fromDate,
            toDate: reportFromData.toDate,
            token,
          }),
        );
      }
    }
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearchValue(e.target.value);
    fetchReports(1, pageLimit, value);
    if (activeTab === 1) {
      await dispatch(
        getOutSourceToRadiologyReport({
          clinicId,
          searchQuery: e.target.value,
          token,
        }),
      );
    } else if (activeTab === 2) {
      await dispatch(
        getOutSourceFromRadiologyReport({
          clinicId,
          searchQuery: e.target.value,
          token,
        }),
      );
    } else {
      await dispatch(
        getAllRadiologyReport({
          clinicId,
          searchQuery: e.target.value,
          token,
        }),
      );
    }
  };

  return (
    <>
      <div className="row d-flex align-items-center">
        <div className="col-md-3">
          <div className="form-group form-focus focused">
            <label className="focus-label">From</label>
            <div className="cal-icon">
              <input
                className="form-control floating datetimepicker"
                type="date"
                value={reportFromData.fromDate}
                onChange={(e) =>
                  setReportFromData({
                    ...reportFromData,
                    fromDate: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group form-focus focused">
            <label className="focus-label">To</label>
            <div className="cal-icon">
              <input
                className="form-control floating datetimepicker"
                type="date"
                value={reportFromData.toDate}
                onChange={(e) =>
                  setReportFromData({
                    ...reportFromData,
                    toDate: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <button
            className="btn btn-primary mt-3"
            onClick={() => handleFilter()}
          >
            Filter
          </button>
        </div>
        <div className={classNames("col-md-3", "mt-4", styles["search-area"])}>
          <input
            type="text"
            className={classNames("form-control", styles["search-input"])}
            placeholder="Search here"
            onChange={(e) => handleSearch(e)}
          />
          <div className="input-group-append">
            <FaSearch className="flaticon-381-search-2" />
          </div>
        </div>
      </div>
      <div className={styles["tabWrapper"]}>
        <div className={styles["tabs"]}>
          <p
            className={activeTab === 0 ? styles["active"] : ""}
            onClick={() => handleTab(0)}
          >
            All Reports
          </p>
          <p
            className={activeTab === 1 ? styles["active"] : ""}
            onClick={() => handleTab(1)}
          >
            Out Source To
          </p>
          <p
            className={activeTab === 2 ? styles["active"] : ""}
            onClick={() => handleTab(2)}
          >
            Out Source From
          </p>
        </div>
        {tableData}
      </div>
    </>
  );
};

export default LabReport;
